import { inject, Injectable, OnDestroy } from '@angular/core';
import { catchError, EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { subjectsComplete, unsubscribeAll } from 'src/app/common/app.helpers';
import { DefaultService } from 'src/app/swagger-api';
import { computeConversationEffectiveTitle } from '../helpers/twilio-message.helpers';
import { ChatConversation } from '../interfaces/chat-model.interface';
import { TitleUpdatedContent } from '../interfaces/chat-operations.interface';

@Injectable({ providedIn: 'root' })
export class ChatTitleService implements OnDestroy {
  private conversationTitleUpdated$: Subject<TitleUpdatedContent> = new Subject<TitleUpdatedContent>();
  private subscriptions$: Subscription[] = [];

  private apiService = inject(DefaultService);

  public ngOnDestroy(): void {
    subjectsComplete([this.conversationTitleUpdated$]);
    unsubscribeAll(this.subscriptions$);
  }

  public get conversationTitleUpdated(): Observable<TitleUpdatedContent> {
    return this.conversationTitleUpdated$.asObservable();
  }

  public resetConversationTitle(conversation: ChatConversation, currentUserId: string): void {
    this.subscriptions$.push(
      this.apiService
        .restSocialConversationTitleDelete(conversation.relatedEntityId)
        .pipe(catchError(() => EMPTY))
        .subscribe({
          next: () => {
            const generatedTitle: string = computeConversationEffectiveTitle(
              null /* customTitle */,
              conversation.participatingUsers,
              currentUserId,
            );
            this.conversationTitleUpdated$.next({
              id: conversation.relatedEntityId,
              newTitle: generatedTitle,
            } as TitleUpdatedContent);
          },
        }),
    );
  }

  public setConversationTitle(conversation: ChatConversation, newTitle: string): void {
    this.subscriptions$.push(
      this.apiService
        .restSocialConversationTitlePut({ title: newTitle }, conversation.relatedEntityId)
        .pipe(catchError(() => EMPTY))
        .subscribe({
          next: () => {
            this.conversationTitleUpdated$.next({
              id: conversation.relatedEntityId,
              newTitle,
            } as TitleUpdatedContent);
          },
        }),
    );
  }
}
